/**
 * Small dashboard widget that nudges the logged-in admin/staff member to
 * finish filling out their own profile. Auto-hides when 100% complete.
 *
 * Powered by the `completion` payload returned from GET /api/staff/profile.
 * Tapping the card jumps the user to the My Profile tab.
 */
import { useEffect, useState } from "react";
import API from "@/lib/api";
import { UserCog, ArrowRight, CheckCircle2 } from "lucide-react";
import StaffAvatar from "@/components/StaffAvatar";

export default function MyProfileCompletionCard({ onJump }) {
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let alive = true;
    API.get("/staff/profile").then(({ data }) => {
      if (alive) setProfile(data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!profile) return null;
  const pct = profile?.completion?.percent ?? 0;
  const missing = profile?.completion?.missing || [];
  const allDone = missing.length === 0;

  // Hide the nudge once everything is in. Keeps the Overview clean for
  // employees who've already done the housekeeping.
  if (allDone) return null;

  const tone = pct >= 80
    ? { ring: "border-emerald-200 dark:border-emerald-900/40", bar: "from-emerald-500 to-emerald-700", text: "text-emerald-700 dark:text-emerald-300" }
    : pct >= 50
      ? { ring: "border-amber-200 dark:border-amber-900/40", bar: "from-amber-500 to-amber-700", text: "text-amber-700 dark:text-amber-300" }
      : { ring: "border-rose-200 dark:border-rose-900/40", bar: "from-rose-500 to-rose-700", text: "text-rose-700 dark:text-rose-300" };

  return (
    <button
      type="button"
      onClick={() => onJump && onJump("myprofile")}
      className={`w-full text-left bg-white dark:bg-slate-900 rounded-xl border ${tone.ring} p-5 hover:shadow-md transition-shadow flex items-center gap-4`}
      data-testid="my-profile-completion-card"
    >
      <StaffAvatar src={profile.avatar_url || ""} name={profile.name} size="lg" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <UserCog className="w-4 h-4 text-purple-500" />
          <p className="text-sm font-semibold text-slate-900 dark:text-white">Your profile is <span className={tone.text}>{pct}% complete</span></p>
          {pct === 100 && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div
            className={`h-full rounded-full bg-gradient-to-r ${tone.bar} transition-all`}
            style={{ width: `${pct}%` }}
            data-testid="overview-completion-bar"
          />
        </div>
        <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 truncate">
          Still needed: <span className="font-medium text-slate-700 dark:text-slate-200">{missing.join(" · ")}</span>
        </p>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
    </button>
  );
}
